import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import useCherryserviceAPI from "../../../api/cherryservice/v2";
import { Subscription as SubscriptionApiObj } from '../../../api/cherryservice/v2/types';
import { SUBSCRIPTION_DAYS_BEFORE_ALERT } from "../../../commons/constants";
import { useDebugLogger, useMounted, useTimer } from "../../../commons/hooks";
import { ButtonColors, FlatButton, TextButton } from '../../../components/buttons';
import {
  InnerListCard,
  ListCardItem,
  ElementCard,
  SmallMetricCard,
  TableCard,
  TableHeader,
  TableContent,
  Column,
  TableColumns,
  TableRows,
  Row,
  Element
} from '../../../components/cards';
import { ListCardDateReminder } from "../../../components/cards/list_cards";
import { Semaphore, SemaphoreColor } from "../../../components/semaphore";
import CopyableIP from "../elements/copyable_ip_box";
import {
  ServerStatus,
  Subscription
} from '../types';

interface FragmentProps {
  subscription: Subscription
  isLocalSubInstalling: boolean
  isAConnectedSubInstalling: boolean
  setModalIsActive: (status: boolean) => void
  manageGetSubscriptionResponse: (res: AxiosResponse<SubscriptionApiObj>) => void
}

const serverStatusMap = new Map<ServerStatus, SemaphoreColor>([
  [ServerStatus.ACTIVE, SemaphoreColor.GREEN],
  [ServerStatus.DECOMMISSIONED, SemaphoreColor.GRAY],
  [ServerStatus.ERROR, SemaphoreColor.RED],
  [ServerStatus.INSTALLING, SemaphoreColor.YELLOW],
  [ServerStatus.PROVISIONING, SemaphoreColor.YELLOW]
])

const SubscriptionCard: React.FC<{ isInstalling: boolean, disableTerminate?: boolean, subscription: Subscription, onTerminateClickCallback: () => void }> = ({ subscription, isInstalling, ...props }) => {
  const [dots, setDots] = useState('');
  const timer = useTimer();
  const mounted = useMounted();

  useEffect(() => {
    if (isInstalling) {
      timer.setTimer(() => {
        if (dots.length === 3) {
          if (mounted) setDots('') //Reset to 0 dots
        } else {
          if (mounted) setDots(dots + '.') //Add a dot
        }
      }, 300)
    } else {
      timer.clearTimer()
      if (mounted) setDots('');
    }
  }, [dots, timer, mounted, isInstalling])

  return (
    <ElementCard
      iconBgColor="bg-white"
      bgColor="bg-gradient-to-tr from-safety-orange-blaze via-red-crayola to-dark-magenta"
      icon={<svg style={{ marginLeft: '-3.5px' }} height="35pt" viewBox="0 -54 512.001 512" width="35pt" xmlns="http://www.w3.org/2000/svg"><path fill="#E94057" d="m417.097656 100.953125c-3.117187 0-6.199218.152344-9.242187.445313-1.441407-56.246094-47.484375-101.398438-104.070313-101.398438-44.566406 0-82.585937 28.011719-97.4375 67.390625-21.800781-18.015625-49.761718-28.835937-80.25-28.835937-69.644531 0-126.097656 56.460937-126.097656 126.113281 0 69.652343 56.453125 126.113281 126.097656 126.113281h124.191406c-.003906-.433594-.015624-.867188-.015624-1.300781 0-38.675781 15.0625-75.035157 42.410156-102.382813s63.707031-42.410156 102.382812-42.410156c38.675782 0 75.035156 15.0625 102.382813 42.410156 5.195312 5.195313 9.945312 10.714844 14.234375 16.515625.207031-2.554687.316406-5.136719.316406-7.746093 0-52.421876-42.488281-94.914063-94.902344-94.914063zm0 0" /><path fill="#8A2387" d="m395.066406 174.878906c-63.292968 0-114.601562 51.308594-114.601562 114.601563s51.308594 114.601562 114.601562 114.601562c63.292969 0 114.597656-51.308593 114.597656-114.601562s-51.304687-114.601563-114.597656-114.601563zm43.902344 176.546875c-12.578125 8.675781-27.328125 13.261719-42.65625 13.261719-29.75 0-55.519531-17.367188-67.707031-42.492188-2.058594 1.953126-3.203125 2.339844-5.144531 2.988282-1.589844.53125-3.203126.785156-4.792969.785156-6.3125 0-12.199219-3.992188-14.3125-10.308594-1.894531-5.664062-.246094-11.65625 3.746093-15.578125 1.9375-2.226562 9.617188-11.691406 16.328126-20.050781 4.191406-5.152344 10.144531-7.828125 17.953124-4.324219l23.832032 10.695313c7.609375 3.414062 11.007812 12.347656 7.59375 19.953125-2.597656 5.789062-8.394532 9.132812-14.371094 8.898437 8.148438 11.621094 21.636719 19.242188 36.875 19.242188 9.179688 0 18-2.742188 25.515625-7.921875 6.863281-4.734375 16.265625-3.003907 21 3.855469 4.734375 6.863281 3.007813 16.265624-3.859375 20.996093zm43.511719-70.804687-14.152344 18.144531c-4.738281 5.78125-9.957031 6.59375-15.660156 5.332031l-26.027344-6.691406c-8.074219-2.078125-12.9375-10.308594-10.859375-18.382812 2.078125-8.074219 10.304688-12.9375 18.378906-10.859376l2.066406.53125c-7.523437-14.386718-22.585937-24.230468-39.914062-24.230468-6.402344 0-12.582031 1.308594-18.363281 3.898437-7.613281 3.40625-16.539063-.003906-19.945313-7.613281s.003906-16.542969 7.613282-19.945312c9.6875-4.335938 20.015624-6.535157 30.699218-6.535157 30.066406 0 56.054688 17.742188 68.082032 43.300781 4.9375-2.214843 10.902343-1.699218 15.46875 1.863282 6.574218 5.128906 7.742187 14.617187 2.613281 21.1875zm0 0" /></svg>}
      fillHeight={true}
    >
      <div>
        <h2 className="text-xl font-header text-gray-50 font-bold">Status</h2>
        <h1 className="text-3xl font-header text-white font-bold pt-2">{subscription.status + dots}</h1>
        <TextButton
          onClick={() => props.onTerminateClickCallback()}
          color={ButtonColors.WHITE}
          disabled={props.disableTerminate}
          className="absolute px-2 py-1 right-2 bottom-2 text-sm">
          TERMINATE
        </TextButton>
      </div>
    </ElementCard>
  );
}

export const DashboardFragment: React.FC<FragmentProps> = ({ subscription, isLocalSubInstalling, isAConnectedSubInstalling, manageGetSubscriptionResponse, setModalIsActive }) => {

  const cherryserviceAPI = useCherryserviceAPI()
  const logger = useDebugLogger()

  function handleAddNode() {
    if (subscription) {
      cherryserviceAPI?.addNodeToSubscription(subscription.id)
        .then(res => manageGetSubscriptionResponse(res))
        .catch(err => err.response && logger.log(err.response))
    }
  }

  return (
    <>
      <div className="mt-8 flex flex-row space-x-8">
        <div className="w-1/3 h-88">
          <InnerListCard
            header="Subscription info"
            autoSize={true}
          >
            <ListCardItem
              title="ID"
              value={`SUB-${subscription.id}`}
              icon={<i className="fas fa-fingerprint text-xl text-gunmetal-200"></i>}
            />
            <ListCardItem
              title="Linked servers"
              value={subscription.servers.length}
              icon={<i className="fas fa-network-wired text-lg text-gunmetal-200"></i>}
            />
            <ListCardItem
              title="Creation date"
              value={subscription.dateCreated.toLocaleDateString()}
              icon={<i className="fas fa-calendar-alt text-lg text-gunmetal-200"></i>}
            />
            <ListCardDateReminder
              title="Expiration date"
              date={subscription.dateEnd} //FIXME
              daysBeforeAlert={subscription.autorenew ? undefined : SUBSCRIPTION_DAYS_BEFORE_ALERT}
              tooltipText={`Your subscription is ${new Date() > subscription.dateEnd ? 'expired' : 'expiring'}, contact sales to extend it`}
            />

          </InnerListCard>
        </div>
        <div className="w-2/3 space-y-8">
          <div className="flex flex-row space-x-8">
            <div className="w-1/2">
              <SubscriptionCard
                subscription={subscription}
                isInstalling={isLocalSubInstalling}
                disableTerminate={isLocalSubInstalling || isAConnectedSubInstalling}
                onTerminateClickCallback={() => setModalIsActive(true)}
              />
            </div>
            <div className="w-1/2">
              <SmallMetricCard
                title="Next bill"
                value="$0"
                icon={<i className="fas fa-credit-card absolute text-white text-4xl"></i>}
                gradient="from-blue-500 to-spring-green"
                comment="Due date: -"
              />
            </div>
          </div>
          <TableCard>
            <TableHeader className={`${subscription.isCluster ? 'flex flex-row justify-between' : 'block'}`}>
              <p className="text-lg 2xs:text-xl font-header text-gunmetal-300 font-bold">Servers</p>
              {
                subscription.isCluster
                  ? <FlatButton disabled={isLocalSubInstalling || isAConnectedSubInstalling} onClick={handleAddNode} className="bg-cherry-red-400 text-white rounded py-1 px-2 text-sm font-bold disabled:bg-gray-400">
                    Add node
                  </FlatButton>
                  : undefined
              }
            </TableHeader>
            <TableContent className="table-fixed">
              <TableColumns className="text-gunmetal-400">
                <Column className="w-4/12">Name</Column>
                <Column className="w-5/12">IP address</Column>
                <Column className="w-3/12">Status</Column>
              </TableColumns>
              <TableRows className="border-1">
                {subscription.servers !== undefined
                  ? subscription.servers.map(server => (
                    <Row className="overflow-hidden" key={server.id}>
                      <Element className="text-gunmetal-200 py-4 border-b-1 border-gray-100 text-sm 2xs:text-base text-left relative">{server.name}</Element>
                      <Element className="text-gunmetal-200 py-4 border-b-1 border-gray-100 text-sm 2xs:text-base text-left relative">
                        <CopyableIP ip={server.ip_address}/>
                      </Element>
                      <Element className="py-4 border-b-1 border-gray-100">
                        <Semaphore
                          color={serverStatusMap.get(server.status) ?? SemaphoreColor.RED}
                          tooltip={server.status}
                        />
                      </Element>
                    </Row>
                  )) : []}
              </TableRows>
            </TableContent>
          </TableCard>
        </div>
      </div>
    </>
  );
}